import { Heading1 } from 'components/shared/Typography.styled';
import { rgba } from 'polished';
import styled from 'styled-components';
import {
  color,
  container,
  fontFamily,
  fontSize,
  fontWeight,
  radius,
  screen,
  spacing
} from 'theme';
import { getPublicPath } from 'utils/paths';

export const StyledIntro = styled.div`
  background-color: ${color('midnight_900')};
  color: ${color('text_white')};
  text-align: center;
`;

export const StyledHero = styled.div`
  background-image: linear-gradient(${rgba('#000', 0)}, ${rgba('#000', 0.7)}),
    url('${getPublicPath('/images/stateSector/2023/hero-bg-sm.webp')}');
  background-size: cover;
  background-position: top center;
  padding: ${spacing('xl4')} ${spacing('md')} ${spacing('md')};
  position: relative;

  @media (min-width: ${screen('md')}) {
    background-image: linear-gradient(${rgba('#000', 0)}, ${rgba('#000', 0.7)}),
      url('${getPublicPath('/images/stateSector/2023/hero-bg.webp')}');
    padding-top: ${spacing('xl5')};
    padding-bottom: ${spacing('xl3')};
  }

  &::after {
    background-image: linear-gradient(transparent, ${color('midnight_900')});
    content: '';
    display: block;
    width: calc(100% + ${spacing('md')});
    height: 20rem;
    position: absolute;
    bottom: 0;
    left: -${spacing('md')};
    right: -${spacing('md')};
  }
`;

export const StyledHeroContainer = styled.div`
  max-width: ${container('lg')};
  margin: auto;
  text-align: center;
  color: ${color('text_white')};
  font-size: ${fontSize('md')};
  line-height: 1.5;
  z-index: 1;
  position: relative;

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('lg')};
    line-height: 1.4;
  }
`;

export const StyledHeroTitle = styled(Heading1)`
  margin-bottom: ${spacing('lg')};
  display: flex;
  flex-direction: column;
  gap: ${spacing('xs')};
  font-size: ${fontSize('xl4')};

  @media (min-width: ${screen('md')}) {
    font-size: ${fontSize('xl7')};
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('xl9')};
  }

  span {
    font-family: ${fontFamily('sans')};
    font-size: ${fontSize('lg')};
    line-height: 1;
    text-transform: uppercase;
    font-weight: ${fontWeight('black')};
    letter-spacing: 0.35em;

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: ${fontSize('xl3')};
    }
  }

  .year {
    font-family: inherit;
    font-size: ${fontSize('xl8')};
    letter-spacing: 0;

    @media (min-width: ${screen('md')}) {
      font-size: ${fontSize('xl10')};
    }

    @media (min-width: ${screen('lg')}) {
      font-size: 10rem;
    }
  }
`;

export const StyledNav = styled.div`
  max-width: ${container('xlMax')};
  margin: auto;
  margin-top: ${spacing('xl4')};
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};
`;

export const StyledNavList = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl2')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${screen('lg')}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }

  a {
    color: inherit;
  }
`;

export const StyledNavItem = styled.div`
  display: flex;
  flex-direction: column;
  font-size: ${fontSize('md')};
  line-height: 1.25;
  font-weight: ${fontWeight('extraBold')};
  border-radius: ${radius('lg')};
  position: relative;
  overflow: hidden;
  transition: 0.3s;
  max-width: 18rem;
  margin: auto;
  aspect-ratio: 1 / 1;

  @media (min-width: ${screen('md')}) {
    max-width: auto;
  }

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('lg')};
    line-height: 1.1666;
  }

  &:hover {
    opacity: 0.9;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }

  span {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background-color: ${color('navy_500')};
    padding: ${spacing('lg')} ${spacing('md')};
    min-height: 4.75em;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

export const StyledData = styled.div`
  max-width: ${container('lg')};
  margin: ${spacing('xl4')} auto;
  font-size: ${fontSize('md')};
  line-height: 1.5;
  padding-left: ${spacing('md')};
  padding-right: ${spacing('md')};

  @media (min-width: ${screen('lg')}) {
    font-size: ${fontSize('lg')};
    line-height: 1.6;
  }
`;

export const StyledDataTitle = styled.h4`
  font-family: ${fontFamily('sans')};
  font-size: ${fontSize('lg')};
  line-height: 1.2;
  text-transform: uppercase;
  letter-spacing: 0.35em;
  margin-top: 0;
  color: ${color('text_subtle')};
  font-weight: ${fontWeight('black')};
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${spacing('xs')};

  &::before,
  &::after {
    content: '';
    height: 1px;
    background-color: ${color('midnight_300')};
    flex-grow: 1;
  }
`;

export const StyledDataList = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${spacing('xl')};
  margin-top: ${spacing('xl2')};

  @media (min-width: ${screen('md')}) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  @media (min-width: ${screen('lg')}) {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
`;

export const StyledDataItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing('md')};
  font-weight: ${fontWeight('black')};
  font-size: ${fontSize('md')};
  line-height: 1.25;

  @media (min-width: ${screen('md')}) {
    flex-direction: column;
    gap: ${spacing('xs')};
  }
`;

export const StyledDataValue = styled.span`
  font-family: ${fontFamily('display')};
  font-weight: ${fontWeight('semiBold')};
  font-size: ${fontSize('xl6')};
  line-height: 1;
  width: 8rem;
  flex-shrink: 0;
  text-align: right;

  @media (min-width: ${screen('md')}) {
    width: auto;
    text-align: center;
  }
`;

export const StyledDataContent = styled.span`
  text-align: left;
  display: flex;
  flex-direction: column-reverse;
  gap: ${spacing('xs')};

  @media (min-width: ${screen('md')}) {
    flex-direction: column;
    text-align: center;
  }

  &::before {
    content: '';
    display: block;
    width: ${spacing('xl4')};
    height: ${spacing('xs')};
    background-color: currentColor;

    @media (min-width: ${screen('md')}) {
      margin-left: auto;
      margin-right: auto;
    }
  }
`;
